import { NO_APLICA } from '@/app/lib/analytics/constants'

import type { SectionNavItem } from './types'

/**
 * `id` de los `<section>` raíz de las cinco secciones a las que salta la barra.
 *
 * Van en el `<section>`, no en el `<h2>`: el `id` del encabezado ya está
 * ocupado por el `aria-labelledby` de la sección y no puede cambiar.
 *
 * Viven aquí, junto a la barra, y no en `templates/PerformancePage`: es la barra
 * la que necesita conocerlos para navegar, y la plantilla los importa para
 * pasarlos a cada sección (plantilla → sección, nunca al revés).
 */
export const PROMOS_SECTION_ID = 'pdp-seccion-promociones'
export const ABOUT_SECTION_ID = 'pdp-seccion-sobre-el-evento'
export const GALLERY_SECTION_ID = 'pdp-seccion-galeria'
export const VENUE_SECTION_ID = 'pdp-seccion-localizacion'
export const REVIEWS_SECTION_ID = 'pdp-seccion-valoraciones'

/**
 * Destinos de la barra, en el orden en que aparecen en la página (el scroll-spy
 * recorre la lista en este orden), que desde la revisión 2026-10-16 sitúa
 * «Promociones» —el resto de promociones— tras la galería. El Figma
 * (`5900:33036`) la mostraba primera. Es una lista de *candidatos*: las
 * secciones de la ficha se omiten cuando no hay dato (FR-004), así que la barra
 * descarta en cliente las que no existen en el DOM. Se hace así, y no
 * replicando aquí las condiciones de vacío de cada sección
 * (`promo.isEmpty`, `items.length === 0`, `!viewModel`…), para que no puedan
 * desincronizarse.
 */
export const SECTION_NAV_ITEMS: readonly SectionNavItem[] = [
  { id: ABOUT_SECTION_ID, label: 'Sobre el evento' },
  { id: GALLERY_SECTION_ID, label: 'Galería' },
  { id: PROMOS_SECTION_ID, label: 'Promociones' },
  { id: VENUE_SECTION_ID, label: 'Localización' },
  { id: REVIEWS_SECTION_ID, label: 'Valoraciones' },
]

/** Nombre accesible de la región de navegación. */
export const SECTION_NAV_ARIA_LABEL = 'Secciones del evento'

/**
 * Con una sola pestaña no hay nada que navegar: la barra taparía contenido sin
 * aportar. Por debajo de este número no se renderiza.
 */
export const MIN_SECTIONS_TO_RENDER = 2

/**
 * Alto de la barra (48px = `--spacing-09`). Se necesita en JS para calcular el
 * umbral del scroll-spy; el valor de verdad sigue siendo el token del SCSS.
 */
export const SECTION_NAV_HEIGHT_PX = 48

/**
 * Tras pulsar una pestaña el scroll suave tarda en llegar. Durante esta ventana
 * el scroll-spy se ignora, para que la pestaña activa no vaya parpadeando por
 * las secciones intermedias que se cruzan de camino. Mismo criterio y valor que
 * `AlphaListingScroller` (`NAVIGATION_SETTLE_MS`).
 */
export const NAVIGATION_SETTLE_MS = 800

/**
 * Respaldo del borde inferior del header cuando `--header-bottom` todavía no
 * está publicado (primer frame antes del `useLayoutEffect` de `Header`). Mismos
 * valores que usa `AlphaListing.module.scss`: 124px con promo abierta en mobile,
 * 68px de header en desktop.
 */
export const FALLBACK_HEADER_BOTTOM_PX = 124

/**
 * Analítica del `ui_interaction` al pulsar una pestaña de la barra de
 * secciones (guía: "tabs" es el ejemplo canónico de `ui_interaction`, frente a
 * `click` reservado a navegación con destino propio — no hay cambio de ruta,
 * solo `scrollIntoView` dentro de la misma ficha, igual que el índice A–Z de
 * `AlphaListing`).
 *
 * ⚠️ PROVISIONAL — pendiente de Analítica. No existe ficha (CSV) en
 * `components-fingerprint/` para esta barra: ni `Tabla Detalles Producto.csv`
 * ni `Ubicación Detalles Producto.csv` (las fichas PDP más próximas) la
 * mencionan. `module_name`/`module_type` son valores propios, centralizados
 * aquí para sustituirlos sin tocar el componente.
 *
 * `module_position: no_aplica` por el único criterio que sí documenta el
 * diccionario para este campo (`analytic-variables.md`: "no_aplica para
 * eventos de chrome"): esta barra no es una sección propia de la ficha sino un
 * elemento persistente que las recorre todas, igual que header/footer.
 */
export const SECTION_NAV_MODULE_NAME = 'navegacion_secciones'
export const SECTION_NAV_MODULE_TYPE = 'section_nav'
export const SECTION_NAV_COMPONENT_ID = 'pdp_section_nav'
export const SECTION_NAV_COMPONENT_LABEL = 'secciones_evento'
export const SECTION_NAV_MODULE_POSITION = NO_APLICA
